import type { Vec2 } from '../../geo/types';
import type { Lot } from './types';

/**
 * A lot seen from its street. The OBB is the footprint the lot planner checked against its
 * neighbours; `Lot.facing` points at the source, up to acos(0.9) off the OBB on a bend, and
 * the OBB lists its corners in an order that depends on which side of the source it lies.
 */
export interface LotFrame {
  /** Middle of the street edge of the OBB. */
  readonly frontMid: Vec2;
  /** Unit vector out of the front toward the street, along an OBB axis. */
  readonly facing: Vec2;
  /** Unit vector along the front, to the right of someone looking at the house from the street. */
  readonly right: Vec2;
  readonly width: number;
  readonly depth: number;
  readonly shear?: number;
}

export interface LotSide {
  /** The corner on the street front. */
  readonly from: Vec2;
  /** The corner at the back of the lot. */
  readonly to: Vec2;
}

export function lotFrame(lot: Lot): LotFrame {
  if (lot.plot) {
    const facing = lot.facing;
    return {
      frontMid: lot.plot.frontMid,
      facing,
      right: [-facing[1], facing[0]],
      width: lot.frontage,
      depth: lot.depth,
      shear: lot.plot.shear,
    };
  }
  const { center, axisU, halfU, halfV } = lot.obb;
  const depthAxis: Vec2 = [-axisU[1], axisU[0]];
  const sign = depthAxis[0] * lot.facing[0] + depthAxis[1] * lot.facing[1] >= 0 ? 1 : -1;
  const facing: Vec2 = [depthAxis[0] * sign, depthAxis[1] * sign];
  return {
    frontMid: [center[0] + facing[0] * halfV, center[1] + facing[1] * halfV],
    facing,
    right: [-facing[1], facing[0]],
    width: 2 * halfU,
    depth: 2 * halfV,
  };
}

/** The two side edges of a lot, named as seen from its street. */
export function lotSides(lot: Lot): { readonly left: LotSide; readonly right: LotSide } {
  const { frontMid, facing, right, width, depth, shear = 0 } = lotFrame(lot);
  const side = (sign: number): LotSide => {
    const from: Vec2 = [
      frontMid[0] + right[0] * sign * (width / 2),
      frontMid[1] + right[1] * sign * (width / 2),
    ];
    return { from, to: [
      from[0] - facing[0] * depth + right[0] * shear * depth,
      from[1] - facing[1] * depth + right[1] * shear * depth,
    ] };
  };
  return { left: side(-1), right: side(1) };
}
